import React, { useState, useMemo, useCallback } from 'react';
import { RiderSummary } from '../types/deliveryHistoryDetail.types';

type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
    columnKey: string | null;
    direction: SortDirection;
}

const SUMMARY_COLUMNS = [
    { key: 'index', label: '순번', sortable: false },
    { key: 'riderId', label: '라이더 ID', sortable: true },
    { key: 'riderName', label: '라이더 이름', sortable: true },
    { key: 'deliveryCount', label: '배달 건수', sortable: true },
    { key: 'totalDeliveryAmt', label: '배달료', sortable: true },
] as const;

const compareValues = <T,>(a: T, b: T, key: keyof T, direction: SortDirection): number => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === 'asc' ? 1 : -1;
    if (bVal == null) return direction === 'asc' ? -1 : 1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal);
    const bStr = String(bVal);
    return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
};

interface SummaryTableProps {
    riderSummaries: RiderSummary[];
}

const SummaryTable: React.FC<SummaryTableProps> = ({ riderSummaries }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>({ columnKey: null, direction: null });

    const handleSort = useCallback((columnKey: string) => {
        if (columnKey === 'index') return;
        setSortConfig((prev) => {
            if (prev.columnKey === columnKey) {
                if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                if (prev.direction === 'desc') return { columnKey: null, direction: null };
            }
            return { columnKey, direction: 'asc' };
        });
    }, []);

    const sortedSummaries = useMemo(() => {
        if (!sortConfig.columnKey || !sortConfig.direction) return riderSummaries;
        return [...riderSummaries].sort((a, b) =>
            compareValues(a, b, sortConfig.columnKey as keyof RiderSummary, sortConfig.direction)
        );
    }, [riderSummaries, sortConfig]);

    const renderSortIcon = (columnKey: string, sortable: boolean) => {
        if (!sortable) return null;
        const isSorted = sortConfig.columnKey === columnKey;
        const direction = isSorted ? sortConfig.direction : null;
        return (
            <span className="sort-icon">
                {direction === 'asc' && <i className="fas fa-sort-up"></i>}
                {direction === 'desc' && <i className="fas fa-sort-down"></i>}
                {!direction && <i className="fas fa-sort" style={{ opacity: 0.3 }}></i>}
            </span>
        );
    };

    return (
        <div className="delivery-history-detail-table-responsive has-summary-header">
            <table className="delivery-history-detail-table summary-table sortable-table">
                <thead>
                <tr>
                    {SUMMARY_COLUMNS.map((col) => (
                        <th
                            key={col.key}
                            className={`${col.sortable ? 'sortable-header' : ''} ${sortConfig.columnKey === col.key ? 'sorted' : ''}`}
                            onClick={() => col.sortable && handleSort(col.key)}
                        >
                            {col.label}
                            {renderSortIcon(col.key, col.sortable)}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {sortedSummaries.length === 0 ? (
                    <tr>
                        <td colSpan={5} className="empty-state">
                            조회된 라이더 집계 내역이 없습니다.
                        </td>
                    </tr>
                ) : (
                    sortedSummaries.map((summary, index) => (
                        <tr key={summary.riderId}>
                            <td>{index + 1}</td>
                            <td>{summary.riderId}</td>
                            <td>{summary.riderName}</td>
                            <td>{summary.deliveryCount.toLocaleString()}</td>
                            <td>{summary.totalDeliveryAmt}원</td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        </div>
    );
};

export default SummaryTable;
